import { Controller } from '@hotwired/stimulus'
import DeviceMobileMixin from './mixins/device_mobile_mixin.js'

export default class OnesignalPushNotificationSetupController extends Controller {
  static values = {
    'userEmail': String,
    'updateUserUrl': String,
    'getDevicesUrl': String,
    'createDeviceUrl': String,
    'enabled': Boolean,
  };
  static targets = ['modal', 'acceptButton', 'laterButton', 'acceptLoader', 'acceptLabel'];
  static DEBUG_MODE = true;

  async connect() {
    if (this.constructor.DEBUG_MODE) {
      // To display messages with console.log, we have to wait for the iOS simulator to activate
      // the web console logs. "Web console logs enabled"
      await new Promise(r => setTimeout(r, 500));
    }

    this._debug('enabled: ' + this.enabledValue);
    if (!this.enabledValue) return;

    const onMobile = await this._onMobile();
    if (!onMobile) return;

    const alreadyAcceptedOrRefused = await this._alreadyAcceptedOrRefused('push_notification');
    this._debug('alreadyAcceptedOrRefused: ' + alreadyAcceptedOrRefused);

    if (!alreadyAcceptedOrRefused) {
      this.softPromptModal = new bootstrap.Modal(this.modalTarget, {
        backdrop: 'static',
        keyboard: false
      });
      this._askForPushNotificationPermission();
    }
  }

  async accept() {
    if (this.isSubmitting) return;

    this._setLoadingState(true, {showAcceptLoader: true});
    try {
      await this._setupPushNotification(true);
      this.softPromptModal.hide();
    } finally {
      this._setLoadingState(false);
    }
  }

  async later() {
    if (this.isSubmitting) return;

    this._setLoadingState(true);
    try {
      await this._setupPushNotification(false);
      this.softPromptModal.hide();
    } finally {
      this._setLoadingState(false);
    }
  }

  _askForPushNotificationPermission() {
    this.softPromptModal.show();
  }

  _setLoadingState(isLoading, options = {}) {
    this.isSubmitting = isLoading;

    if (this.hasAcceptButtonTarget) {
      this.acceptButtonTarget.disabled = isLoading;
    }

    if (this.hasLaterButtonTarget) {
      this.laterButtonTarget.disabled = isLoading;
    }

    const showAcceptLoader = isLoading && options.showAcceptLoader;

    if (this.hasAcceptLoaderTarget) {
      this.acceptLoaderTarget.classList.toggle('d-none', !showAcceptLoader);
    }

    if (this.hasAcceptLabelTarget) {
      this.acceptLabelTarget.classList.toggle('d-none', showAcceptLoader);
    }
  }

  async _setupPushNotification(accept) {
    if (accept) {
      await this._registerUser();
    }
    const info = await this._getDeviceInfo();
    let params = {
      onesignal_subscription_id: info.subscriptionId,
      installation_id: info.installationId,
      device_name: info.deviceName,
    };
    if (accept) {
      params['push_notification_accepted_at'] = new Date();
    } else {
      params['push_notification_refused_at'] = new Date();
    }
    await this._registerDevice(params);
  }

  async _registerUser() {
    const oneSignalId = await this._ensureOneSignalRegistered();
    if (oneSignalId) {
      this._debug('oneSignalId: ' + oneSignalId);
      try {
        const response = await fetch(this.updateUserUrlValue, {
          method: 'PUT',
          body: JSON.stringify({
            user: {
              onesignal_id: oneSignalId,
            },
          }),
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
            'Content-Type': 'application/json',
          },
        });
        this._debug('BO response code: ' + response.status);
        this._handleServerError(response);
      } catch (error) {
        this._debug('Error registering device: ' + error.message);
        this._handleServerError();
      }
    }
  }

  async _ensureOneSignalRegistered() {
    // Grant user privacy consent (GDPR compliance)
    await Median.onesignal.userPrivacyConsent.grant();

    // Trigger the mobile native prompt for push notification permission
    await Median.onesignal.register();

    const anonymousInfo = await this._waitForDeviceInfo(
      (info) => Boolean(info?.oneSignalId),
      {description: 'anonymous oneSignalId'}
    );

    // The OneSignal ID is guaranteed to remain the same across multiple installs and installs across multiple devices
    // as long as you assign the same user the same identifier (e.g email/phone/account number) by "logging in" the user
    // to OneSignal with the median.onesignal.login() method. This strategy provides ease of use as OneSignal tracks all devices
    // belonging to a single user and you can simply target users instead of targeting devices (and manually associating devices to users).
    const externalId = this.userEmailValue?.trim();
    if (externalId) {
      const loggedIn = await Median.onesignal.login(externalId);
      this._debug(`login response: ${JSON.stringify(loggedIn)}`);

      if (loggedIn?.success) {
        const info = await this._waitForDeviceInfo(
          (deviceInfo) => Boolean(deviceInfo?.oneSignalId),
          {description: 'identified oneSignalId'}
        );
        return info?.oneSignalId;
      }
    }

    return anonymousInfo?.oneSignalId || null;
  }

  async _waitForDeviceInfo(condition, options = {}) {
    const timeoutMs = options.timeoutMs || 10000;
    const intervalMs = options.intervalMs || 300;
    const description = options.description || 'device info';
    const startedAt = Date.now();

    while ((Date.now() - startedAt) < timeoutMs) {
      const info = await this._getDeviceInfo();
      if (condition(info)) {
        this._debug(`Ready ${description}: ${JSON.stringify(info)}`);
        return info;
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    this._debug(`Timeout while waiting for ${description}`);
    return null;
  }
}

Object.assign(OnesignalPushNotificationSetupController.prototype, DeviceMobileMixin);
